import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import type { StaffAction } from '../core/api/api.client';
import { AuthService } from '../core/auth/auth.service';
import { ClientMeService } from '../core/client/client-me.service';
import { initialsOf } from '../core/domain/person';
import { ProducerService } from '../core/producer/producer.service';

export { producerNavSelection } from './producer-nav.selection';

interface ProducerNavItem {
  readonly path: string;
  readonly label: string;
  readonly action?: StaffAction;
}

interface ProducerNavGroup {
  readonly title: string;
  readonly items: readonly ProducerNavItem[];
}

interface ProducerDockTab {
  readonly path: string;
  readonly label: string;
  readonly action?: StaffAction;
  readonly icon: 'situation' | 'scan' | 'orders' | 'products';
}

/**
 * Dock bas producteur — quatre raccourcis opérationnels + avatar profil
 * qui ouvre le menu complet (tous les écrans).
 */
const DOCK_TABS: readonly ProducerDockTab[] = [
  { path: '/producteur/tableau-de-bord', label: 'Situation', icon: 'situation' },
  { path: '/producteur/scan', label: 'Scanner', action: 'order.confirm_pickup', icon: 'scan' },
  { path: '/producteur/commandes', label: 'Commandes', action: 'order.read', icon: 'orders' },
  { path: '/producteur/produits', label: 'Gamme', action: 'product.write', icon: 'products' },
];

const MORE_GROUPS: readonly ProducerNavGroup[] = [
  {
    title: 'Compte',
    items: [{ path: '/profil', label: 'Mon profil' }],
  },
  {
    title: 'Vendre',
    items: [
      { path: '/producteur/produits', label: 'Ma gamme', action: 'product.write' },
      { path: '/producteur/points-de-retrait', label: 'Points de retrait', action: 'pickup.write' },
    ],
  },
  {
    title: 'Retraits',
    items: [
      { path: '/producteur/commandes', label: 'Commandes', action: 'order.read' },
      { path: '/producteur/scan', label: 'Scanner', action: 'order.confirm_pickup' },
    ],
  },
  {
    title: 'Boutique',
    items: [
      { path: '/producteur/affiche', label: 'Affiche', action: 'shop.read' },
      { path: '/producteur/boutique', label: 'À votre image', action: 'product.write' },
    ],
  },
  {
    title: 'Exploitation',
    items: [
      { path: '/producteur/tableau-de-bord', label: 'Situation' },
      { path: '/producteur/paliers', label: 'Palier' },
      { path: '/producteur/equipe', label: 'Équipe', action: 'staff.manage' },
      { path: '/producteur/avis', label: 'Avis', action: 'review.read' },
      { path: '/producteur/succes', label: 'Succès', action: 'review.read' },
      { path: '/producteur/statistiques', label: 'Statistiques', action: 'stats.read' },
    ],
  },
];

@Component({
  selector: 'ct-producer-nav',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, RouterLinkActive],
  template: `
    <nav class="dock" aria-label="Espace producteur">
      @for (tab of visibleTabs(); track tab.path) {
        <a
          class="tab"
          [routerLink]="tab.path"
          routerLinkActive="active"
          [attr.aria-label]="tab.label"
        >
          <span class="icon" aria-hidden="true">
            @switch (tab.icon) {
              @case ('situation') {
                <svg viewBox="0 0 24 24" width="22" height="22">
                  <path
                    fill="currentColor"
                    d="M3 13h2v8H3v-8zm4-6h2v14H7V7zm4-4h2v18h-2V3zm4 8h2v10h-2V11zm4-5h2v15h-2V6z"
                  />
                </svg>
              }
              @case ('scan') {
                <svg viewBox="0 0 24 24" width="22" height="22">
                  <path
                    fill="currentColor"
                    d="M3 3h6v2H5v4H3V3zm12 0h6v6h-2V5h-4V3zM3 15h2v4h4v2H3v-6zm16 0h2v6h-6v-2h4v-4zM7 7h4v4H7V7zm6 0h4v2h-2v2h-2V7zm-6 6h2v2H7v-2zm4 0h6v2h-2v2h-2v-2h-2v-2zm-2 4h2v2H9v-2zm8-2h2v4h-4v-2h2v-2z"
                  />
                </svg>
              }
              @case ('orders') {
                <svg viewBox="0 0 24 24" width="22" height="22">
                  <path
                    fill="currentColor"
                    d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-9 14-5-5 1.4-1.4L10 14.2l7.6-7.6L19 8l-9 9z"
                  />
                </svg>
              }
              @case ('products') {
                <svg viewBox="0 0 24 24" width="22" height="22">
                  <path
                    fill="currentColor"
                    d="M4 6h6v5H4V6zm10 0h6v5h-6V6zM4 13h6v5H4v-5zm10 0h6v5h-6v-5zM3 4h8v1H3V4zm10 0h8v1h-8V4zM3 19h8v1H3v-1zm10 0h8v1h-8v-1z"
                  />
                </svg>
              }
            }
          </span>
          <span class="caption">{{ tab.label }}</span>
        </a>
      }

      <button
        type="button"
        class="tab profile"
        [class.active]="moreOpen() || menuRouteActive()"
        [attr.aria-expanded]="moreOpen()"
        aria-controls="producer-more-sheet"
        [attr.aria-label]="profileLabel()"
        (click)="toggleMore()"
      >
        @if (photoUrl(); as url) {
          <img class="avatar" [src]="url" alt="" aria-hidden="true" />
        } @else {
          <span class="avatar initials" aria-hidden="true">{{ initials() }}</span>
        }
        <span class="caption">Profil</span>
      </button>
    </nav>

    @if (moreOpen()) {
      <div class="sheet-backdrop" (click)="closeMore()" aria-hidden="true"></div>
      <div
        id="producer-more-sheet"
        class="sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Menu producteur"
      >
        <header class="sheet-head">
          <div class="sheet-who">
            <h2>{{ displayName() }}</h2>
            <p class="ct-muted">Menu producteur</p>
          </div>
          <button type="button" class="sheet-close" aria-label="Fermer" (click)="closeMore()">
            ×
          </button>
        </header>
        @for (group of visibleMoreGroups(); track group.title) {
          <section class="sheet-group">
            <h3>{{ group.title }}</h3>
            <ul>
              @for (item of group.items; track item.path) {
                <li>
                  <a [routerLink]="item.path" (click)="closeMore()">{{ item.label }}</a>
                </li>
              }
            </ul>
          </section>
        }
      </div>
    }
  `,
  styles: `
    :host {
      display: contents;
    }

    .dock {
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 40;
      display: flex;
      align-items: stretch;
      justify-content: space-around;
      gap: 0.125rem;
      padding: 0.35rem 0.35rem calc(0.35rem + env(safe-area-inset-bottom, 0px));
      background: var(--ct-white);
      border-top: 1px solid var(--ct-border);
      box-shadow: 0 -0.5rem 1.5rem rgb(61 46 34 / 8%);
    }

    .tab {
      flex: 1 1 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.15rem;
      min-width: 0;
      min-height: 3.25rem;
      padding: 0.25rem 0.15rem;
      border: 0;
      border-radius: var(--ct-radius-md);
      background: transparent;
      color: var(--ct-text-muted);
      text-decoration: none;
      font: inherit;
      cursor: pointer;

      &:hover {
        color: var(--ct-vert-cageot);
        background: var(--ct-kraft);
      }

      &:focus-visible {
        outline: 2px solid var(--ct-focus);
        outline-offset: 2px;
      }

      &.active {
        color: var(--ct-vert-cageot);
        font-weight: var(--ct-weight-bold);
      }
    }

    .icon {
      display: grid;
      place-items: center;
      width: 1.5rem;
      height: 1.5rem;
    }

    .caption {
      font-size: 0.65rem;
      line-height: 1.1;
      text-align: center;
    }

    .avatar {
      width: 1.65rem;
      height: 1.65rem;
      border-radius: 50%;
      object-fit: cover;
      display: block;
    }

    .avatar.initials {
      display: grid;
      place-items: center;
      background: var(--ct-vert-cageot);
      color: var(--ct-kraft);
      font-family: var(--ct-font-title);
      font-size: 0.7rem;
      font-weight: var(--ct-weight-bold);
    }

    .sheet-backdrop {
      position: fixed;
      inset: 0;
      z-index: 45;
      background: rgb(61 46 34 / 45%);
    }

    .sheet {
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 50;
      max-height: min(70dvh, 32rem);
      overflow: auto;
      padding: var(--ct-space-4) var(--ct-space-4)
        calc(var(--ct-space-4) + env(safe-area-inset-bottom, 0px));
      background: var(--ct-white);
      border-radius: var(--ct-radius-lg) var(--ct-radius-lg) 0 0;
      box-shadow: 0 -1rem 2.5rem rgb(0 0 0 / 18%);
    }

    .sheet-head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: var(--ct-space-3);
      margin-bottom: var(--ct-space-3);
    }

    .sheet-who {
      min-width: 0;

      h2 {
        margin: 0;
        font-size: var(--ct-size-lg);
        color: var(--ct-vert-cageot);
      }

      p {
        margin: 0.15rem 0 0;
        font-size: var(--ct-size-sm);
      }
    }

    .sheet-close {
      width: 2.25rem;
      height: 2.25rem;
      border: 1px solid var(--ct-border);
      border-radius: var(--ct-radius-sm);
      background: var(--ct-kraft);
      font-size: 1.35rem;
      line-height: 1;
      color: var(--ct-text-muted);
      cursor: pointer;
      flex-shrink: 0;
    }

    .sheet-group {
      margin-bottom: var(--ct-space-3);

      h3 {
        margin: 0 0 var(--ct-space-1);
        font-size: var(--ct-size-xs);
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: var(--ct-text-muted);
      }

      ul {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 0.125rem;
      }

      a {
        display: block;
        padding: var(--ct-space-2) var(--ct-space-3);
        border-radius: var(--ct-radius-sm);
        color: var(--ct-text);
        text-decoration: none;

        &:hover {
          background: var(--ct-kraft);
        }
      }
    }

    @media print {
      .dock,
      .sheet,
      .sheet-backdrop {
        display: none !important;
      }
    }
  `,
})
export class CtProducerNav {
  private readonly router = inject(Router);
  private readonly producer = inject(ProducerService);
  private readonly auth = inject(AuthService);
  private readonly clientMe = inject(ClientMeService);

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
    ),
    { requireSync: true },
  );

  protected readonly moreOpen = signal(false);

  protected readonly photoUrl = computed(() => this.clientMe.photoUrl());
  protected readonly initials = computed(() => {
    const profile = this.auth.profile();
    return initialsOf(profile?.fullName ?? profile?.email ?? '?');
  });
  protected readonly displayName = computed(() => {
    const profile = this.auth.profile();
    return profile?.fullName?.trim() || profile?.email || 'Profil';
  });
  protected readonly profileLabel = computed(() => `Menu — ${this.displayName()}`);

  protected readonly visibleTabs = computed(() => {
    this.producer.membership();
    return DOCK_TABS.filter((tab) => this.isVisible(tab));
  });

  protected readonly visibleMoreGroups = computed(() => {
    this.producer.membership();
    const groups: ProducerNavGroup[] = [];
    for (const group of MORE_GROUPS) {
      const items = group.items.filter((item) => this.isVisible(item));
      if (items.length > 0) {
        groups.push({ title: group.title, items });
      }
    }
    return groups;
  });

  /** Profil actif hors raccourcis dock (palier, équipe, affiche, profil…). */
  protected readonly menuRouteActive = computed(() => {
    const path = (this.url().split('?')[0] ?? '').split('#')[0] ?? '';
    const onDockShortcut = this.visibleTabs().some(
      (tab) => path === tab.path || path.startsWith(`${tab.path}/`),
    );
    if (onDockShortcut) {
      return false;
    }
    return path === '/profil' || path.startsWith('/producteur');
  });

  constructor() {
    void this.producer.ensureLoaded();
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.moreOpen.set(false));
  }

  protected toggleMore(): void {
    this.moreOpen.update((open) => !open);
  }

  protected closeMore(): void {
    this.moreOpen.set(false);
  }

  private isVisible(item: { readonly action?: StaffAction }): boolean {
    return item.action === undefined || this.producer.can(item.action);
  }
}
